import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import axios from "axios";
import * as S from "./styles";
import Toolbar from "./Toolbar";
import ItemSelectionPanel from "./ItemSelectionPanel";
import DateSelectionPanel from "./DateSelectionPanel";
import TableView from "./TableView";
import CalendarView from "./CalendarView";
import {
  Box,
  CircularProgress,
  Snackbar,
  Alert,
  Typography,
} from "@mui/material";
import TPC_URL from "../../../utils/TPC_URL";

dayjs.locale("ko");

const getStatusText = (code) => {
  const statusMap = {
    0: "대기",
    1: "요청됨",
    2: "작업 중",
    3: "완료",
    8: "취소 요청됨",
    9: "취소됨",
    "-1": "오류",
  };
  return statusMap[code] || "알 수 없음";
};

function ResultGrid() {
  const { group_code, project_code } = useParams();
  const [summaryData, setSummaryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [itemsData, setItemsData] = useState([]);
  const [stockData, setStockData] = useState({});
  const [orderData, setOrderData] = useState([]);
  const [chartInstance, setChartInstance] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedCategories, setExpandedCategories] = useState({});
  const [viewMode, setViewMode] = useState("table");
  const [instructionData, setInstructionData] = useState([]);
  const [shiftData, setShiftData] = useState([]);
  const [planDataWithShift, setPlanDataWithShift] = useState([]);
  const [itemIdToMaterialGrouping, setItemIdToMaterialGrouping] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [statusCode, setStatusCode] = useState(null);

  const getInitialDateRange = () => {
    try {
      const storageKey = `resultGrid_dateRange_${group_code}_${project_code}`;
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        setUserModifiedDateRange(true);
        return {
          start: dayjs(parsed.start),
          end: dayjs(parsed.end),
        };
      }
    } catch (error) {
      console.error("Error loading date range from localStorage:", error);
    }
    return {
      start: dayjs(),
      end: dayjs().add(14, "day"),
    };
  };

  const [dateRange, setDateRange] = useState(getInitialDateRange);
  const [presetPeriod, setPresetPeriod] = useState("custom");
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [isItemPanelOpen, setIsItemPanelOpen] = useState(false);
  const [isDatePanelOpen, setIsDatePanelOpen] = useState(false);
  const [isPanelPinned, setIsPanelPinned] = useState(false);
  const calendarRef = useRef(null);
  const mainContentRef = useRef(null);
  const [userModifiedDateRange, setUserModifiedDateRange] = useState(false);

  useEffect(() => {
    if (userModifiedDateRange) {
      try {
        const storageKey = `resultGrid_dateRange_${group_code}_${project_code}`;
        localStorage.setItem(
          storageKey,
          JSON.stringify({
            start: dateRange.start.format("YYYY-MM-DD"),
            end: dateRange.end.format("YYYY-MM-DD"),
          })
        );
      } catch (error) {
        console.error("Error saving date range to localStorage:", error);
      }
    }
  }, [dateRange, group_code, project_code, userModifiedDateRange]);

  const fetchOrderData = useCallback(async () => {
    try {
      setDataLoading(true);
      const start_date = dateRange.start.format("YYYY-MM-DD");
      const end_date = dateRange.end.format("YYYY-MM-DD");
      const response = await axios.get(
        TPC_URL +
          `/api/tpc/getOrderByDate?group_code=${group_code}&project_code=${project_code}&start_date=${start_date}&end_date=${end_date}`
      );
      if (response.data && Array.isArray(response.data)) {
        setOrderData(response.data);
      } else {
        setOrderData([]);
      }
    } catch (error) {
      console.error("Error fetching order data:", error);
      setOrderData([]);
    } finally {
      setDataLoading(false);
    }
  }, [group_code, project_code, dateRange]);

  const fetchResultData = useCallback(async () => {
    try {
      setDataLoading(true);
      const response = await axios.get(
        TPC_URL +
          `/api/tpc/getResultData?group_code=${group_code}&project_code=${project_code}`
      );
      const response2 = await axios.get(
        TPC_URL +
          `/api/instruction/getInstPerformancce?group_code=${group_code}&project_code=${project_code}`
      );
      const response3 = await axios.get(
        TPC_URL +
          `/api/shift/getAllShifts?group_code=${group_code}&project_code=${project_code}`
      );
      const shiftRes = response3.data || [];
      setInstructionData(response2.data || []);
      setShiftData(shiftRes);
  
      if (response.data && Array.isArray(response.data)) {
        const itemsMap = {};
        const categoriesMap = {};
        const planData = response.data.filter((item) => item.type === "plan");
  
        setPlanDataWithShift(planData);
  
        const idToGroupMap = {};
        planData.forEach((item) => {
          const materialGroup = item.material_grouping || "기타";
          idToGroupMap[item.item_id] = materialGroup;
        });
        setItemIdToMaterialGrouping(idToGroupMap);
  
        // ✅ 추가: item_code별 안전재고 추적
        const safetyStockByItemCode = new Map();
  
        planData.forEach((item) => {
          const materialGroup = item.material_grouping || "기타";
  
          if (!itemsMap[materialGroup]) {
            itemsMap[materialGroup] = {
              id: materialGroup,
              name: materialGroup,
              code: materialGroup,
              materialGroup: materialGroup,
              safetyStock: 0,
              color: item.gantt_color || "#000000",
              productionPlan: {},
              itemIds: [],
              itemCodes: [],
            };
          }
          
          // ✅ 수정: item_code별로 안전재고 저장 후 합산
          if (item.item_code && item.safety_stock) {
            const key = `${materialGroup}_${item.item_code}`;
            if (!safetyStockByItemCode.has(key)) {
              safetyStockByItemCode.set(key, item.safety_stock);
            }
          }
          
          if (!itemsMap[materialGroup].itemIds.includes(item.item_id)) {
            itemsMap[materialGroup].itemIds.push(item.item_id);
          }
          if (
            item.item_code &&
            !itemsMap[materialGroup].itemCodes.includes(item.item_code)
          ) {
            itemsMap[materialGroup].itemCodes.push(item.item_code);
          }
          
          const planDate = item.prod_date_only || item.prod_date;
          
          if (!itemsMap[materialGroup].productionPlan[planDate]) {
            itemsMap[materialGroup].productionPlan[planDate] = 0;
          }
          itemsMap[materialGroup].productionPlan[planDate] += item.qty;
          const category = materialGroup;
          if (!categoriesMap[category]) {
            categoriesMap[category] = [];
          }
          if (!categoriesMap[category].includes(materialGroup)) {
            categoriesMap[category].push(materialGroup);
          }
        });
  
        // ✅ 추가: material_grouping별로 안전재고 합산
        Object.keys(itemsMap).forEach((materialGroup) => {
          let totalSafetyStock = 0;
          
          safetyStockByItemCode.forEach((safetyStock, key) => {
            if (key.startsWith(`${materialGroup}_`)) {
              totalSafetyStock += safetyStock;
            }
          });
          
          itemsMap[materialGroup].safetyStock = totalSafetyStock;
          
          console.log(`[안전재고] ${materialGroup}: ${totalSafetyStock}`);
        });
  
        const stockIncomingData = response.data.filter(
          (item) => item.type === "stock"
        );
        window.incomingStockData = stockIncomingData;
  
        const initialExpandedCategories = {};
        Object.keys(categoriesMap).forEach((category, index) => {
          initialExpandedCategories[category] = index === 0;
        });
        setExpandedCategories(initialExpandedCategories);
  
        const itemsArray = Object.values(itemsMap);
        setItemsData(itemsArray);
  
        if (itemsArray.length > 0) {
          setSelectedItems([itemsArray[0].id.toString()]);
        }
        await fetchStockData();
        await fetchOrderData();
      }
    } catch (error) {
      console.error("Error fetching result data:", error);
    } finally {
      setDataLoading(false);
    }
  }, [group_code, project_code, fetchOrderData]);
  
  const fetchStockData = useCallback(async () => {
    try {
      const response = await axios.get(
        TPC_URL +
          `/api/stock/getSummary?group_code=${group_code}&project_code=${project_code}`
      );

      if (response.data) {
        const stockMap = {};
        response.data.forEach((item) => {
          stockMap[item.item_code] = Number(item.total_stock_quantity);
        });
        setStockData(stockMap);
      }
    } catch (error) {
      console.error("Error fetching stock data:", error);
    }
  }, [group_code, project_code]);

  const itemCategories = useMemo(() => {
    const categories = {};
    itemsData.forEach((item) => {
      const category = item.materialGroup || "기타";
      if (!categories[category]) {
        categories[category] = [];
      }
      categories[category].push(item.id.toString());
    });
    return categories;
  }, [itemsData]);

  const displayDates = useMemo(() => {
    const dates = [];
    let currentDate = dateRange.start.clone();
    const endDate = dateRange.end;
    while (
      currentDate.isBefore(endDate) ||
      currentDate.isSame(endDate, "day")
    ) {
      dates.push(currentDate.format("YYYY-MM-DD"));
      currentDate = currentDate.add(1, "day");
    }
    return dates;
  }, [dateRange]);

  const chartDateLabels = useMemo(() => {
    return displayDates.map((date) => {
      const d = dayjs(date);
      return d.format("MM/DD");
    });
  }, [displayDates]);

  const orderByDateAndItem = useMemo(() => {
    const result = {};
    displayDates.forEach((date) => {
      result[date] = {};
      selectedItems.forEach((materialGroup) => {
        result[date][materialGroup] = 0;
      });
    });
    orderData.forEach((order) => {
      const materialGroup = itemIdToMaterialGrouping[order.item_id];
      if (materialGroup && selectedItems.includes(materialGroup)) {
        const dueDate = dayjs(order.due_date).format("YYYY-MM-DD");
        if (displayDates.includes(dueDate)) {
          result[dueDate][materialGroup] =
            (result[dueDate][materialGroup] || 0) + order.qty;
        }
      }
    });
    return result;
  }, [orderData, displayDates, selectedItems, itemIdToMaterialGrouping]);

  const calculateInstructionIncoming = useCallback(
    (materialGroup, firstPlanDate) => {
      if (!instructionData.length || !firstPlanDate) return 0;

      const oneDayBefore = dayjs(firstPlanDate)
        .subtract(1, "day")
        .format("YYYY-MM-DD");
      const itemIds =
        itemsData.find((item) => item.id === materialGroup)?.itemIds || [];

      const filteredInstructions = instructionData.filter((instruction) => {
        if (!itemIds.includes(instruction.item_id)) return false;
        if (instruction.ai_qty === 0 && instruction.custom_qty === 0)
          return false;
        const instructionDate = dayjs(instruction.instruction_date).format(
          "YYYY-MM-DD"
        );
        return instructionDate <= oneDayBefore;
      });
      const totalB = filteredInstructions.reduce((sum, instruction) => {
        const remaining =
          (instruction.custom_qty || 0) - (instruction.complete_qty || 0);
        return sum + remaining;
      }, 0);

      return totalB;
    },
    [instructionData, itemsData]
  );

  const generateSummaryData = useCallback(() => {
    if (!itemsData.length || !displayDates.length) return [];
    const selectedItemsData = itemsData.filter((item) =>
      selectedItems.includes(item.id.toString())
    );
    const initialStock = selectedItemsData.reduce((acc, item) => {
      let totalStock = 0;
      if (item.itemCodes && Array.isArray(item.itemCodes)) {
        item.itemCodes.forEach((itemCode) => {
          totalStock += stockData[itemCode] || 0;
        });
      }
      return acc + totalStock;
    }, 0);
    const safetyStock = selectedItemsData.reduce((acc, item) => {
      return acc + (item.safetyStock || 0);
    }, 0);
    const dailyProduction = {};
    const dailyStock = {};
    const dailyShipment = {};
    const dailyIncoming = {};
    let totalProduction = 0;
    let totalShipment = 0;
    let totalIncoming = 0;

    displayDates.forEach((date) => {
      dailyProduction[date] = 0;
      dailyShipment[date] = 0;
      dailyIncoming[date] = 0;
      if (date === displayDates[0]) {
        dailyStock[date] = initialStock;
      } else {
        dailyStock[date] = 0;
      }
    });

    selectedItemsData.forEach((item) => {
      const productionDates = Object.keys(item.productionPlan).sort();
      const firstPlanDate = productionDates[0];

      if (firstPlanDate && displayDates.includes(firstPlanDate)) {
        const instructionIncoming = calculateInstructionIncoming(
          item.id,
          firstPlanDate
        );
        dailyIncoming[firstPlanDate] += instructionIncoming;
        totalIncoming += instructionIncoming;
      }

      Object.entries(item.productionPlan).forEach(([date, qty]) => {
        if (displayDates.includes(date)) {
          dailyProduction[date] += qty;
          totalProduction += qty;
        }
      });
    });

    displayDates.forEach((date) => {
      selectedItems.forEach((materialGroup) => {
        const shipmentQty = orderByDateAndItem[date]?.[materialGroup] || 0;
        dailyShipment[date] += shipmentQty;
        totalShipment += shipmentQty;
      });
    });
    
    // ✅ 입고예정 데이터 처리 (수정)
    if (window.incomingStockData) {
      const incomingMap = new Map();
      
      window.incomingStockData.forEach((stock) => {
        const materialGroup = itemIdToMaterialGrouping[stock.item_id];
        if (materialGroup && selectedItems.includes(materialGroup)) {
          // ✅ stock_date_only 우선 사용
          const stockDate = stock.stock_date_only 
            || stock.prod_date 
            || dayjs(stock.stock_date).format("YYYY-MM-DD");
          
          if (displayDates.includes(stockDate)) {
            const key = `${stock.item_id}_${stockDate}`;
            
            if (!incomingMap.has(key)) {
              incomingMap.set(key, {
                item_id: stock.item_id,
                stock_date: stockDate,
                materialGroup: materialGroup,
                qty: 0,
              });
            }
            const existing = incomingMap.get(key);
            existing.qty += Number(stock.qty) || 0;
          }
        }
      });
      
      incomingMap.forEach((item) => {
        const stockDate = item.stock_date;
        if (displayDates.includes(stockDate)) {
          dailyIncoming[stockDate] = (dailyIncoming[stockDate] || 0) + item.qty;
          totalIncoming += item.qty;
        }
      });
    }
    
    const adjustedInitialStock = initialStock;
    let currentStock = adjustedInitialStock;
    displayDates.forEach((date, index) => {
      if (index === 0) {
        currentStock = adjustedInitialStock + dailyIncoming[date] - dailyShipment[date];
      } else {
        const previousDate = displayDates[index - 1];
        const previousStock = dailyStock[previousDate] || 0;
        currentStock = previousStock + dailyIncoming[date] - dailyShipment[date];
      }
      dailyStock[date] = currentStock;
    });

    const summaryRows = [
      {
        id: "summary-initialStock",
        type: "현재재고",
        value: adjustedInitialStock,
        dates: { [displayDates[0]]: adjustedInitialStock },
      },
      {
        id: "summary-production",
        type: "생산계획",
        value: totalProduction,
        dates: dailyProduction,
      },
      {
        id: "summary-incoming",
        type: "입고예정",
        value: totalIncoming,
        dates: dailyIncoming,
      },
      {
        id: "summary-shipment",
        type: "출고예정",
        value: totalShipment,
        dates: dailyShipment,
      },
      {
        id: "summary-estimatedStock",
        type: "예상재고",
        value: dailyStock[displayDates[displayDates.length - 1]],
        dates: dailyStock,
      },
      {
        id: "summary-safetyStock",
        type: "안전재고",
        value: safetyStock,
        dates: displayDates.reduce(
          (acc, date) => ({ ...acc, [date]: safetyStock }),
          {}
        ),
      },
    ];
    
    // ✅ shift별 생산량 계산 (수정)
    shiftData.forEach((shift) => {
      const shiftDailyProduction = {};
      let shiftTotalProduction = 0;
      displayDates.forEach((date) => {
        shiftDailyProduction[date] = 0;
      });
      const normalizedShiftId = shift.shift_id;

      planDataWithShift
        .filter((item) => {
          if (item.in_stock_id) return false;
          const materialGroup = item.material_grouping || "기타";
          if (!selectedItems.includes(materialGroup)) return false;
          const itemShiftId = Number(item.shift_id) || 0;
          const effectiveShiftId = itemShiftId === 0 ? 1 : itemShiftId;
          if (effectiveShiftId !== normalizedShiftId) return false;
          return true;
        })
        .forEach((item) => {
          // ✅ prod_date_only 사용!
          const planDate = item.prod_date_only || dayjs(item.prod_date).format("YYYY-MM-DD");
          
          if (displayDates.includes(planDate)) {
            const qty = Number(item.qty) || 0;
            if (!isNaN(qty) && qty > 0) {
              shiftDailyProduction[planDate] =
                (shiftDailyProduction[planDate] || 0) + qty;
              shiftTotalProduction += qty;
            }
          }
        });

      summaryRows.push({
        id: `summary-shift-${shift.shift_id}`,
        type: shift.shift_name,
        value: shiftTotalProduction,
        dates: shiftDailyProduction,
      });
    });

    return summaryRows;
  }, [
    itemsData,
    selectedItems,
    displayDates,
    stockData,
    orderByDateAndItem,
    calculateInstructionIncoming,
    shiftData,
    planDataWithShift,
    itemIdToMaterialGrouping,
  ]);

  const generateCalendarEvents = useCallback((summaryData) => {
    if (!summaryData.length) return [];
    const events = [];

    const productionData = summaryData.find((item) => item.type === "생산계획");
    if (productionData) {
      Object.entries(productionData.dates).forEach(([date, qty]) => {
        if (qty > 0) {
          events.push({
            id: `production-${date}`,
            title: `생산: ${qty}`,
            start: date,
            classNames: ["production-event"],
            extendedProps: { type: "production", value: qty },
          });
        }
      });
    }

    const shipmentData = summaryData.find((item) => item.type === "출고예정");
    if (shipmentData) {
      Object.entries(shipmentData.dates).forEach(([date, qty]) => {
        if (qty > 0) {
          events.push({
            id: `output-${date}`,
            title: `출고: ${qty}`,
            start: date,
            classNames: ["output-event"],
            extendedProps: { type: "output", value: qty },
          });
        }
      });
    }

    return events;
  }, []);
  
  const toggleAllCategories = (expanded) => {
    const newExpanded = {};
    Object.keys(itemCategories).forEach(category => {
      newExpanded[category] = expanded;
    });
    setExpandedCategories(newExpanded);
  };
  
  const generateChartOption = useCallback(
    (summaryData, chartDateLabels) => {
      if (!summaryData || !summaryData.length) return {};
      const series = [];
  
      const productionData = summaryData.find(
        (item) => item.type === "생산계획"
      );
      if (productionData) {
        const data = chartDateLabels.map((label, index) => {
          const dateStr = displayDates[index];
          return productionData.dates[dateStr] || 0;
        });
        series.push({
          name: "생산계획",
          type: "bar",
          yAxisIndex: 0,
          data: data,
          itemStyle: { color: "#6d81a3" },
        });
      }
  
      const incomingData = summaryData.find((item) => item.type === "입고예정");
      if (incomingData) {
        const data = chartDateLabels.map((label, index) => {
          const dateStr = displayDates[index];
          return incomingData.dates[dateStr] || 0;
        });
        series.push({
          name: "입고예정",
          type: "bar",
          yAxisIndex: 0,
          data: data,
          itemStyle: { color: "#9dcf84" },
        });
      }
  
      const shipmentData = summaryData.find((item) => item.type === "출고예정");
      if (shipmentData) {
        const data = chartDateLabels.map((label, index) => {
          const dateStr = displayDates[index];
          return shipmentData.dates[dateStr] || 0;
        });
        series.push({
          name: "출고예정",
          type: "bar",
          yAxisIndex: 0,
          data: data,
          itemStyle: { color: "#96550f" },
        });
      }
  
      const stockData = summaryData.find((item) => item.type === "예상재고");
      if (stockData) {
        const data = chartDateLabels.map((label, index) => {
          const dateStr = displayDates[index];
          return stockData.dates[dateStr] || 0;
        });
        series.push({
          name: "예상재고",
          type: "line",
          yAxisIndex: 1,
          data: data,
          symbolSize: 6,
          lineStyle: { width: 3 },
          itemStyle: { color: "#027878" },
        });
      }
  
      const safetyStockData = summaryData.find(
        (item) => item.type === "안전재고"
      );
      if (safetyStockData) {
        const data = chartDateLabels.map((label, index) => {
          const dateStr = displayDates[index];
          return safetyStockData.dates[dateStr] || 0;
        });
        series.push({
          name: "안전재고",
          type: "line",
          yAxisIndex: 1,
          data: data,
          symbolSize: 0,
          lineStyle: { width: 2, type: "dashed" },
          itemStyle: { color: "#f5222d" },
        });
      }
  
      const categoryWidth = 120;
      const categoryCount = chartDateLabels.length;
      const totalChartWidth = categoryWidth * categoryCount;
  
      return {
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "shadow" },
        },
        legend: {
          data: series.map((s) => s.name),
          bottom: 0,
          type: "scroll",
        },
        grid: {
          left: 60,
          right: 60,
          bottom: "80px",
          top: "30px",
          containLabel: false,
          width: totalChartWidth,
        },
        xAxis: {
          type: "category",
          boundaryGap: true,
          data: chartDateLabels,
          axisLabel: {
            interval: 0,
            rotate: 0,
          },
        },
        yAxis: [
          {
            type: "value",
            name: "수량 (생산/출고)",
            nameLocation: "end",
            position: "left",
            alignTicks: true,
            axisLine: {
              show: true,
              lineStyle: {
                color: "#6d81a3",
              },
            },
            axisLabel: {
              formatter: "{value}",
              color: "#6d81a3",
            },
          },
          {
            type: "value",
            name: "재고량",
            nameLocation: "end",
            position: "right",
            alignTicks: true,
            axisLine: {
              show: true,
              lineStyle: {
                color: "#027878",
              },
            },
            axisLabel: {
              formatter: "{value}",
              color: "#027878",
            },
            splitLine: {
              show: false,
            },
          },
        ],
        series: series,
        barGap: "20%",
        barCategoryGap: "40%",
      };
    },
    [displayDates]
  );
    
  const summaryColumns = useMemo(() => {
    const cols = [
      {
        accessorKey: "type",
        header: "항목",
        size: 120,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
      },
      {
        accessorKey: "value",
        header: "합계",
        size: 80,
        Cell: ({ cell }) => cell.getValue() || "",
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
      },
    ];
  
    displayDates.forEach((date) => {
      const d = dayjs(date);
      const formattedDate = d.format("MM월 DD일");
      const dayOfWeek = d.format("ddd");
      const headerText = `${formattedDate} (${dayOfWeek})`;
      
      const isWeekend = d.day() === 0 || d.day() === 6;
      
      cols.push({
        accessorFn: (row) => row.dates?.[date] ?? "",
        id: date,
        header: headerText,
        size: 120,
        muiTableHeadCellProps: { 
          align: "center",
          sx: isWeekend ? { 
            backgroundColor: "#fff3e0"
          } : {}
        },
        muiTableBodyCellProps: { 
          align: "center",
          sx: isWeekend ? { 
            backgroundColor: "#fff9f0" 
          } : {}
        },
        Cell: ({ cell, row }) => {
          const value = cell.getValue();
          if (value === "") return "";
          if (row.original.type === "예상재고") {
            const safetyStockRow = summaryData.find(
              (item) => item.type === "안전재고"
            );
            const safetyStockValue = safetyStockRow?.dates?.[date] || 0;
            if (value <= safetyStockValue) {
              return (
                <span style={{ color: "red", fontWeight: "bold" }}>
                  {value}
                </span>
              );
            }
          }
          return value;
        },
      });
    });
    
    return cols;
  }, [displayDates, summaryData]);
  
  const getFilteredItemsInCategory = useCallback(
    (category) => {
      if (!itemCategories[category]) return [];
      if (!searchTerm) return itemCategories[category];
      return itemCategories[category].filter((materialGroup) => {
        const searchLower = searchTerm.toLowerCase();
        return materialGroup.toLowerCase().includes(searchLower);
      });
    },
    [itemCategories, searchTerm]
  );

  const filteredCategories = useMemo(() => {
    if (!searchTerm) return Object.keys(itemCategories);
    return Object.keys(itemCategories).filter((category) => {
      if (category.toLowerCase().includes(searchTerm.toLowerCase()))
        return true;
      return getFilteredItemsInCategory(category).length > 0;
    });
  }, [itemCategories, searchTerm, getFilteredItemsInCategory]);

  const calculatedSummaryData = useMemo(
    () => generateSummaryData(),
    [generateSummaryData]
  );

  const currentChartOption = useMemo(
    () => generateChartOption(calculatedSummaryData, chartDateLabels),
    [calculatedSummaryData, chartDateLabels, generateChartOption]
  );

  const checkStatus = useCallback(async () => {
    try {
      const res = await axios.get(
        TPC_URL +
          `/api/tpc/status?group_code=${group_code}&project_code=${project_code}`
      );
      const code = res.data?.status?.code;
      setStatusCode(code);
    } catch (error) {
      console.error("Error checking status:", error);
      setStatusCode(null);
    }
  }, [group_code, project_code]);

  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      try {
        await fetchResultData();
        await checkStatus();
      } finally {
        setLoading(false);
      }
    };
    loadInitialData();
  }, [fetchResultData, checkStatus]);

  useEffect(() => {
    if (!loading) {
      setSummaryData(calculatedSummaryData);
    }
  }, [calculatedSummaryData, loading]);

  useEffect(() => {
    if (calculatedSummaryData.length) {
      setCalendarEvents(generateCalendarEvents(calculatedSummaryData));
    }
  }, [calculatedSummaryData, generateCalendarEvents]);

  useEffect(() => {
    if (chartInstance) {
      chartInstance.setOption(currentChartOption, true);
    }
  }, [currentChartOption, chartInstance]);

  useEffect(() => {
    if (!loading) {
      fetchOrderData();
    }
  }, [fetchOrderData, loading, dateRange]);

  useEffect(() => {
    if (viewMode === "table" && !userModifiedDateRange && itemsData.length > 0 && selectedItems.length > 0 && !loading) {
      const allDates = new Set();
      
      selectedItems.forEach((materialGroup) => {
        const item = itemsData.find((i) => i.id === materialGroup);
        if (item && item.productionPlan) {
          Object.keys(item.productionPlan).forEach((date) => {
            if (item.productionPlan[date] > 0) {
              allDates.add(date);
            }
          });
        }
      });
      
      planDataWithShift.forEach((item) => {
        if (!item.in_stock_id) {
          const materialGroup = item.material_grouping || "기타";
          if (selectedItems.includes(materialGroup)) {
            const planDate = item.prod_date_only || dayjs(item.prod_date).format("YYYY-MM-DD");
            allDates.add(planDate);
          }
        }
      });
      
      if (window.incomingStockData) {
        window.incomingStockData.forEach((stock) => {
          const materialGroup = itemIdToMaterialGrouping[stock.item_id];
          if (materialGroup && selectedItems.includes(materialGroup)) {
            const stockDate = stock.stock_date_only 
              || stock.prod_date 
              || dayjs(stock.stock_date).format("YYYY-MM-DD");
            allDates.add(stockDate);
          }
        });
      }
      
      if (allDates.size > 0) {
        const sortedDates = Array.from(allDates).sort((a, b) => {
          return dayjs(a).diff(dayjs(b));
        });
        const earliestDate = sortedDates[0];
        
        const currentDuration = dateRange.end.diff(dateRange.start, "day");
        const newStart = dayjs(earliestDate);
        const newEnd = newStart.add(currentDuration, "day");
        
        if (!newStart.isSame(dateRange.start, "day")) {
          setDateRange({
            start: newStart,
            end: newEnd,
          });
        }
      }
    }
  }, [viewMode, userModifiedDateRange, itemsData, selectedItems, planDataWithShift, loading, dateRange, itemIdToMaterialGrouping]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isItemPanelOpen &&
        !isPanelPinned &&
        !event.target.closest(".item-panel") &&
        !event.target.closest(".item-panel-toggle")
      ) {
        setIsItemPanelOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isItemPanelOpen, isPanelPinned]);

  const onChartReady = useCallback((echarts) => setChartInstance(echarts), []);

  const toggleItemPanel = () => {
    if (isDatePanelOpen) setIsDatePanelOpen(false);
    setIsItemPanelOpen(!isItemPanelOpen);
  };

  const toggleDatePanel = () => {
    if (isItemPanelOpen) setIsItemPanelOpen(false);
    setIsDatePanelOpen(!isDatePanelOpen);
  };

  const togglePanelPin = () => {
    setIsPanelPinned(!isPanelPinned);
  };

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) setViewMode(newMode);
  };

  const handleToggleItem = (itemId) => {
    setDataLoading(true);
    setTimeout(() => {
      setSelectedItems((prev) => {
        if (prev.includes(itemId)) {
          return prev.length > 1 ? prev.filter((id) => id !== itemId) : prev;
        }
        return [...prev, itemId];
      });
      setDataLoading(false);
    }, 300);
  };

  const handleSelectAllInCategory = (category, isSelected) => {
    setDataLoading(true);
    setTimeout(() => {
      const categoryItems = itemCategories[category] || [];
      if (isSelected) {
        const newSelected = selectedItems.filter(
          (id) => !categoryItems.includes(id)
        );
        if (newSelected.length > 0) {
          setSelectedItems(newSelected);
        }
      } else {
        const itemsToAdd = categoryItems.filter(
          (id) => !selectedItems.includes(id)
        );
        setSelectedItems([...selectedItems, ...itemsToAdd]);
      }
      setDataLoading(false);
    }, 300);
  };

  const clearAllSelections = () => {
    setDataLoading(true);
    setTimeout(() => {
      const firstCategory = Object.keys(itemCategories)[0];
      if (firstCategory && itemCategories[firstCategory].length > 0) {
        setSelectedItems([itemCategories[firstCategory][0]]);
      } else {
        setSelectedItems([]);
      }
      setDataLoading(false);
    }, 300);
  };

  const clearSearch = () => setSearchTerm("");

  const toggleCategory = (category) => {
    setExpandedCategories((prev) => ({ ...prev, [category]: !prev[category] }));
  };

  const handlePresetChange = (preset) => {
    setPresetPeriod(preset);
    setUserModifiedDateRange(true);
    const today = dayjs();
    let newStart, newEnd;
    switch (preset) {
      case "1week":
        newStart = today;
        newEnd = today.add(7, "day");
        break;
      case "2weeks":
        newStart = today;
        newEnd = today.add(14, "day");
        break;
      case "1month":
        newStart = today.startOf("month");
        newEnd = today.endOf("month");
        break;
      case "3months":
        newStart = today;
        newEnd = today.add(3, "month");
        break;
      default:
        return;
    }
    setDateRange({ start: newStart, end: newEnd });
    if (calendarRef.current && viewMode === "calendar") {
      calendarRef.current.getApi().gotoDate(newStart.toDate());
    }
  };

  const [tempDateRange, setTempDateRange] = useState(dateRange);

  useEffect(() => {
    setTempDateRange(dateRange);
  }, [dateRange]);

  const handleStartDateChange = (newDate) => {
    setPresetPeriod("custom");
    setTempDateRange((prev) => ({ ...prev, start: newDate }));
  };

  const handleEndDateChange = (newDate) => {
    setPresetPeriod("custom");
    setTempDateRange((prev) => ({ ...prev, end: newDate }));
  };

  const applyDateRange = () => {
    setUserModifiedDateRange(true);
    setDateRange(tempDateRange);
    setIsDatePanelOpen(false);
    if (calendarRef.current && viewMode === "calendar") {
      setTimeout(() => {
        if (calendarRef.current) {
          calendarRef.current.getApi().gotoDate(tempDateRange.start.toDate());
        }
      }, 0);
    }
  };

  const handleCalendarDateSet = (dateInfo) => {};

  const handleEventClick = (clickInfo) => {
    const { type, value } = clickInfo.event.extendedProps;
    const dateStr = dayjs(clickInfo.event.startStr).format("YYYY년 MM월 DD일");
    let typeText = "";
    switch (type) {
      case "production":
        typeText = "생산계획";
        break;
      case "input":
        typeText = "입고예정";
        break;
      case "output":
        typeText = "출고예정";
        break;
      default:
        typeText = "일정";
    }
    alert(`${dateStr} ${typeText}: ${value}`);
  };

  const getItemName = useCallback((materialGroup) => {
    return materialGroup || `Unknown (${materialGroup})`;
  }, []);

  return (
    <S.Container>
      <ItemSelectionPanel
        isOpen={isItemPanelOpen}
        isPinned={isPanelPinned}
        onClose={() => setIsItemPanelOpen(false)}
        onTogglePin={togglePanelPin}
        itemCategories={itemCategories}
        selectedItems={selectedItems}
        expandedCategories={expandedCategories}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onToggleAllCategories={toggleAllCategories}
        onClearSearch={clearSearch}
        onToggleCategory={toggleCategory}
        onToggleItem={handleToggleItem}
        onSelectAllInCategory={handleSelectAllInCategory}
        onClearAllSelections={clearAllSelections}
        getItemName={getItemName}
        filteredCategories={filteredCategories}
        getFilteredItemsInCategory={getFilteredItemsInCategory}
      />
      <DateSelectionPanel
        isOpen={isDatePanelOpen}
        isPinned={isPanelPinned}
        onClose={() => setIsDatePanelOpen(false)}
        onTogglePin={togglePanelPin}
        presetPeriod={presetPeriod}
        onPresetChange={handlePresetChange}
        tempDateRange={tempDateRange}
        onStartDateChange={handleStartDateChange}
        onEndDateChange={handleEndDateChange}
        onApply={applyDateRange}
      />
      <S.MainContent ref={mainContentRef}>
        <Toolbar
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
          onToggleItemPanel={toggleItemPanel}
          onToggleDatePanel={toggleDatePanel}
          selectedItemsCount={selectedItems.length}
          dateRange={dateRange}
        />
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="400px"
          >
            <CircularProgress />
          </Box>
        ) : statusCode !== 3 ? (
          <S.ContentWrapper style={{ position: "relative" }}>
            <S.DimOverlay>
              <S.DimContent>
                <Typography variant="h5" color="error" gutterBottom>
                  생산계획 수립에 오류가 발생했습니다.
                </Typography>
              </S.DimContent>
            </S.DimOverlay>
            <S.ContentWrapper>
              {viewMode === "table" ? (
                <TableView
                  summaryData={summaryData}
                  summaryColumns={summaryColumns}
                  chartDateLabels={chartDateLabels}
                  currentChartOption={currentChartOption}
                  onChartReady={onChartReady}
                  dataLoading={dataLoading}
                  selectedItems={selectedItems}
                  itemsData={itemsData}
                  displayDates={displayDates}
                />
              ) : (
                <CalendarView
                  calendarRef={calendarRef}
                  calendarEvents={calendarEvents}
                  onDateSet={handleCalendarDateSet}
                  onEventClick={handleEventClick}
                />
              )}
            </S.ContentWrapper>
          </S.ContentWrapper>
        ) : (
          <S.ContentWrapper>
            {viewMode === "table" ? (
              <TableView
                summaryData={summaryData}
                summaryColumns={summaryColumns}
                chartDateLabels={chartDateLabels}
                currentChartOption={currentChartOption}
                onChartReady={onChartReady}
                dataLoading={dataLoading}
                selectedItems={selectedItems}
                itemsData={itemsData}
                displayDates={displayDates}
              />
            ) : (
              <CalendarView
                calendarRef={calendarRef}
                calendarEvents={calendarEvents}
                onDateSet={handleCalendarDateSet}
                onEventClick={handleEventClick}
              />
            )}
          </S.ContentWrapper>
        )}
      </S.MainContent>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </S.Container>
  );
}

export default ResultGrid;
